<script setup lang="ts">
import { type FrontendPathwayEvent, useOutOfSpecialCurrency, usePathwayEvent } from '@/composables/game/pathway-events'
import ChevronLeftIcon from '@/components/icons/ChevronLeftIcon.vue'
import { RouteName } from '@/routes'
import { computed, h, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'
import type { Step } from '@/types/shared/pathway-event'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { usePlayer } from '@/composables/game/player'
import { useConfetti } from '@/composables/game/confetti'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useConfig } from '@/composables/game/config'
import { useToaster } from '@/composables/game/toasters'
import { useGiveaway } from '@/composables/game/giveaway'
import { useRouter } from 'vue-router'
import SpinsConfettiImage from '@/assets/images/fortune-wheel.webp'
import CoinConfettiImage from '@/assets/images/icons/gold-coin-icon-s3.webp'
import TicketConfettiImage from '@/assets/images/emojis/emoji-ticket.png'
import StarsConfettiImage from '@/assets/images/premium.png'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import { formatDuration } from '@/helpers/date'
import api from '@/services/api'
import ClientDealStep from '@/components/ClientDealStep.vue'
import ClientDealPurchaseStepModal from '@/components/modals/ClientDealPurchaseStepModal.vue'
import ClientDealCompletionOverlay from '@/components/overlays/ClientDealCompletionOverlay.vue'
import OutOfSpecialCurrencyModal from '@/components/modals/OutOfSpecialCurrencyModal.vue'
import ExplodeEmojisAnimation from '@/components/ExplodeEmojisAnimation.vue'

const props = defineProps<{
	clientDeal: FrontendPathwayEvent
}>()

const emit = defineEmits(['back'])

const { coins, updatePlayer, player } = usePlayer()
const { syncConfig } = useConfig()
const { availableSpins, updateState: updateFortuneWheelState } = useFortuneWheel()
const { countdownForEventEnd, playerProgress, totalRewards } = usePathwayEvent(props.clientDeal)
const { showConfetti } = useConfetti()
const { addHapticFeedback } = useHapticFeedback()
const { notify } = useToaster()
const {
  playerTickets,
  syncGiveaway,
} = useGiveaway()
const {
  selectedSpecialCurrencyForModal,
  canShowOutOfSpecialCurrencyModal,
  getAvailableEcommerceItems,
  closeOutOfSpecialCurrencyModal,
} = useOutOfSpecialCurrency()
const router = useRouter()

const backpackScaleIn = ref(true)
const backpackBounce = computed(() => showBackpackAnimation.value)
const showClaimStepModal = ref(false)
const loadingStepIndex = ref<null | number>(null)

const selectedStep = ref<Step & { index: number; } | null>(null)
const showCompletionOverlay = ref(false)
const stepRefs = useTemplateRef('step-items')
const stepsListRef = useTemplateRef('stepsList')

const rewardShapeImageMap = computed<Record<string, string>>(() => ({
  spins: SpinsConfettiImage,
  goldenCoins: CoinConfettiImage,
  giveawayTickets: TicketConfettiImage,
  dropeeStars: StarsConfettiImage,
  ...Object.fromEntries(
    Object.entries(allSpecialCurrenciesConfig.value).map(([key, value]) => [key, value.image]),
  ),
}))

const goldCircleAnimationItems = ref<Record<string, boolean>>({
  spins: false,
  goldenCoins: false,
  giveawayTickets: false,
  dropeeStars: false,
  ...Object.fromEntries(
    Object.keys(allSpecialCurrenciesConfig.value).map(id => [id, false]),
  ),
})

const lastWonRewardImage = ref<string>('')
const showBackpackAnimation = computed(() => {
  return Object.values(goldCircleAnimationItems.value).some(Boolean)
})

type CostMapEntry = {
	type: string,
	amount: number,
	count: number
}

function showGoldenCircles(reward: Record<string, number | Partial<Record<string, number>>>) {
  Object.keys(goldCircleAnimationItems.value).forEach(key => {
    goldCircleAnimationItems.value[key as keyof typeof goldCircleAnimationItems.value] = false
  })

  setTimeout(() => {
    Object.keys(reward).forEach(rewardType => {
      let key = ''
      const isSpecialCurrency = rewardType === 'specialCurrencies'
      if (isSpecialCurrency) {
        const specialCurrencies = reward.specialCurrencies as Record<string, number>
        if (specialCurrencies) {
          key = Object.keys(specialCurrencies).find(key => specialCurrencies[key]) ?? ''
        }
      } else {
        key = rewardType
      }
      lastWonRewardImage.value = rewardShapeImageMap.value[key] ?? ''
      goldCircleAnimationItems.value[key] = true
    })
  }, 100)
}

function getConfettiShapes(step: Step) {
  return {
    count: 150,
    spread: 100,
    startVelocity: 45,
    decay: 0.9,
    shapes: ['image'],
    shapeOptions: {
      image: getConfettiImages(step),
    },
  }
}

function getConfettiImages(step: Step) {
  const images: ReturnType<typeof getConfettiImageConfig>[] = []

  Object.keys(step.reward).forEach(rewardType => {
    let imageKey = ''
    const isSpecialCurrency = rewardType === 'specialCurrencies'
    if (isSpecialCurrency) {
      const specialCurrencies = step.reward.specialCurrencies
      if (specialCurrencies) {
        imageKey = Object.keys(specialCurrencies).find(key => (specialCurrencies as Record<string, number>)[key]) ?? ''
      }
    } else {
      imageKey = rewardType
    }
    const imageSrc = rewardShapeImageMap.value[imageKey]
    if (imageSrc) {
      images.push(getConfettiImageConfig(imageSrc))
    }
  })
  return images
}

function getConfettiImageConfig(imageSrc: string) {
  return {
    src: imageSrc,
    width: 24,
    height: 24,
    particles: {
      size: {
        value: 12,
      },
    },
  }
}

async function onSuccessfulClaim(step: Step) {
  addHapticFeedback('medium')
  showGoldenCircles(step.reward)
  await showConfetti({ ...getConfettiShapes(step) })
  const { playerStats } = await syncConfig()
  updatePlayer(playerStats)
  await updateFortuneWheelState()
  await syncGiveaway()
  await scrollToNextStep()
  if (step.reward.goldenCoins) {
    nonReactiveCoins.value = coins.value
  }
}

async function scrollToNextStep() {
  await nextTick()
  const nextStepIndex = playerProgress.value.currentStep
  const stepElements = stepRefs.value as Array<{ $el: HTMLElement } | HTMLElement> | null
  const stepsListContainer = stepsListRef.value as HTMLElement | null

  if (stepElements && stepElements[nextStepIndex] && stepsListContainer) {
    const stepItem = stepElements[nextStepIndex]
    const nextStepElement = (stepItem && typeof stepItem === 'object' && '$el' in stepItem) ? stepItem.$el : stepItem as HTMLElement

    const containerRect = stepsListContainer.getBoundingClientRect()
    const elementRect = nextStepElement.getBoundingClientRect()

    const elementTopRelativeToContainer = elementRect.top - containerRect.top
    const currentScrollTop = stepsListContainer.scrollTop

    const elementAbsoluteTop = currentScrollTop + elementTopRelativeToContainer

    const containerHeight = stepsListContainer.clientHeight
    const elementHeight = elementRect.height
    const targetScrollPosition = elementAbsoluteTop - (containerHeight / 2) + (elementHeight / 2)

    stepsListContainer.scrollTo({
      top: Math.max(0, targetScrollPosition),
      behavior: 'smooth',
    })
  } else {
    console.log('Scroll failed: missing elements')
  }
}

const nonReactiveCoins = ref(coins.value)

const balanceSummary = computed((): Record<string, number> => {
  return {
    spins: availableSpins.value,
    goldenCoins: nonReactiveCoins.value,
    giveawayTickets: playerTickets.value,
    dropeeStars: player.value.ecommerce.starsBalance,
    ...Object.fromEntries(
      Object.keys(allSpecialCurrenciesConfig.value).map(id => [
        id,
        (player.value.specialCurrencies as Record<string, number>)[id] ?? 0,
      ]),
    ),
  }
})

function addOrUpdateCostInMap(
  map: Map<string, CostMapEntry>,
  key: string,
  type: string,
  amount: number,
) {
  const existing = map.get(key)
  if (existing) {
    existing.count++
    existing.amount += amount
  } else {
    map.set(key, { type, amount, count: 1 })
  }
}

const mostFrequentCosts = computed((): Array<{
	type: string,
	amount: number
}> => {
  const allCosts = Object.values(props.clientDeal.config.steps ?? {})
    .map(step => step.cost)
    .filter(cost => cost.type === 'specialCurrency' || cost.type === 'star')

  const costMap = new Map<string, CostMapEntry>()

  for (const cost of allCosts) {
    if (cost.type === 'specialCurrency') {
      const currencyId = cost.currencyId
      const amount = cost.amount
      if (amount != null && amount > 0) {
        addOrUpdateCostInMap(costMap, `specialCurrency:${currencyId}`, currencyId, amount)
      }
    } else if (cost.type === 'star') {
      const amount = cost.amount
      if (amount != null && amount > 0) {
        addOrUpdateCostInMap(costMap, 'dropeeStars', 'dropeeStars', amount)
      }
    }
  }

  return Array.from(costMap.values())
    .map(entry => ({
      type: entry.type,
      amount: entry.amount,
      playerBalance: balanceSummary.value[entry.type],
    }))
    .sort((a, b) => b.playerBalance - a.playerBalance)
    .slice(0, 2)
    .map(entry => ({ type: entry.type, amount: entry.amount }))
})

function isStepDone(index: number) {
  return playerProgress.value.currentStep > index
}

function isStepClaimable(index: number) {
  return !isStepDone(index) && playerProgress.value.currentStep === index
}

function closeClaimStepModal() {
  showClaimStepModal.value = false
  setTimeout(() => selectedStep.value = null, 100)
}

async function onPayForPathway(eventId: string, step: Step, index: number) {
  loadingStepIndex.value = index
  try {
    await api.payForPathwayEvent({ eventId, stepNumber: index + 1 })
    await onSuccessfulClaim(step)
  } catch (e) {
    notify({ type: 'error', title: 'Failed to claim' })
  } finally {
    loadingStepIndex.value = null
  }
}

function onStepPurchase(step: Step, index: number) {
  selectedStep.value = { ...step, index }
}

function onOutOfSpecialCurrency(currencyId: string) {
  if (canShowOutOfSpecialCurrencyModal(currencyId)) {
    selectedSpecialCurrencyForModal.value = currencyId
    return
  }
  notify({
    type: 'error',
    title: `Not enough <img width="20px" height="20px" style="display: inline-block; vertical-align: middle;" src="${rewardShapeImageMap.value[currencyId]}" alt="Icon">`,
    description: h('span', { class: 'font-medium' }, [
      h('span', {
        class: 'underline text-accent1-default',
        onClick: () => {
          router.push({ name: RouteName.REWARDS })
        },
      }, 'Spin the wheel'),
      ' to get more now!',
    ]),
  })
}

const allStepsDone = computed(() => {
  const totalSteps = Object.keys(props.clientDeal.config.steps).length
  return playerProgress.value.currentStep >= totalSteps
})

function onCompletionOverlayHide() {
  showCompletionOverlay.value = false
  emit('back')
}

watch(allStepsDone, (isDone) => {
  if (isDone) {
    setTimeout(() => {
      showCompletionOverlay.value = true
      showConfetti()
    }, 500)
  }
})

watch(selectedStep, selectedStep => {
  if (!selectedStep) return
  setTimeout(() => showClaimStepModal.value = true, 100)
})

onMounted(async () => {
  await syncGiveaway()
  setTimeout(() => {
    backpackScaleIn.value = false
  }, 1000)
})
</script>

<template>
  <div class="flex flex-col w-full h-full absolute inset-0">
    <div class="flex-none px-4 pt-6">
      <div class="w-full flex items-center gap-x-3">
        <ChevronLeftIcon
          class="size-10 min-w-[40px]"
          @click="$emit('back')"
        />
        <div class="flex-1 flex items-center gap-x-2">
          <div class="relative">
            <div
              class="size-10 min-w-[40px] rounded-full bg-dark-top-layer/[.66] backdrop-blur-[12px]"
              :class="[{ 'bounce-up': backpackBounce, 'scale-in': backpackScaleIn }]"
              :style="backpackBounce ? { animationDelay: '2.2s' } : {}"
              @click="router.push({ name: RouteName.INVENTORY })"
            >
              <img
                class="w-full h-full object-cover"
                src="@/assets/images/inventory.webp"
              >
            </div>
            <ExplodeEmojisAnimation
              class="pointer-events-none"
              :show="showBackpackAnimation"
              :image-src="lastWonRewardImage"
              :loop="1"
              reverse
            />
          </div>
          <div
            v-for="(cost, index) in mostFrequentCosts"
            :key="index"
            class="rounded-full bg-dark-top-layer/[.66] backdrop-blur-[12px] pl-3 pr-[14px] py-2 flex items-center gap-x-1.5 scale-in"
          >
            <div class="relative flex h-6 w-6 min-w-[24px] justify-center">
              <img
                class="h-6 w-auto object-cover"
                :src="rewardShapeImageMap[cost.type]"
              >
            </div>

            <span class="text-light-white text-base font-semibold">
              {{ formatNumberFullTruncate(balanceSummary[cost.type], false, false, false) }}
            </span>
          </div>
        </div>
        <CrossCircleIcon
          class="size-8 min-w-[32px] text-white/[87%]"
          @click="router.push({ name: RouteName.HOME })"
        />
      </div>

      <div class="w-full mt-3 flex flex-col items-center">
        <img
          :src="clientDeal.mainIcon"
          class="size-[144px]"
        >
        <div
          class="mt-3 text-center text-light-white font-pilat text-[32px] leading-[36px] font-bold text-shadow-2 uppercase"
        >
          {{ clientDeal.eventName }}
        </div>
        <div
          class="-mx-4 mt-6 w-[calc(100%+32px)] h-[30px] flex items-center justify-center gap-x-2 border-y border-y-[#F29C11A8] bg-[#F29C11A8]"
        >
          <span class="text-white/[.87] text-base font-bold font-pilat">ENDS IN</span>
          <img
            class="size-[18px] object-cover"
            src="@/assets/images/gold-clock.png"
          >
          <div class="golden-text-black-stroke text-base font-bold gold-text-shadow px-0.5 min-w-[76px]">
            {{ formatDuration(countdownForEventEnd) }}
          </div>
        </div>
      </div>
    </div>

    <div
      ref="stepsList"
      class="pt-6 flex-1 w-full overflow-y-auto px-4 pb-6"
    >
      <div class="flex flex-col gap-y-4">
        <ClientDealStep
          v-for="(step, index) in Object.values(clientDeal.config.steps)"
          ref="step-items"
          :key="index"
          :index="index"
          :event-id="clientDeal.id"
          :step="step"
          :is-done="isStepDone(index)"
          :is-last-step="index === Object.keys(clientDeal.config.steps).length - 1"
          :is-claimable="isStepClaimable(index)"
          :is-loading="loadingStepIndex === index"
          @on-pay="onPayForPathway(clientDeal.id, step, index)"
          @on-purchase="onStepPurchase(step, index)"
          @out-of-special-currency="onOutOfSpecialCurrency"
        />
      </div>
    </div>

    <ClientDealPurchaseStepModal
      v-if="selectedStep && showClaimStepModal"
      :show="showClaimStepModal"
      :step="selectedStep"
      :client-deal="clientDeal"
      @purchase-done="onSuccessfulClaim(selectedStep)"
      @hide="closeClaimStepModal"
    />

    <ClientDealCompletionOverlay
      v-if="showCompletionOverlay"
      :show="showCompletionOverlay"
      :client-deal="clientDeal"
      :total-rewards="totalRewards"
      @hide="onCompletionOverlayHide"
    />

    <OutOfSpecialCurrencyModal
      v-if="selectedSpecialCurrencyForModal"
      :show="!!selectedSpecialCurrencyForModal"
      :currency="selectedSpecialCurrencyForModal"
      :ecommerce-items="getAvailableEcommerceItems(selectedSpecialCurrencyForModal)"
      @hide="closeOutOfSpecialCurrencyModal($event)"
    />
  </div>
</template>

<style scoped lang="scss">
.bounce-up {
	animation: backpack-bounce-up 0.6s cubic-bezier(.68, -0.55, .27, 1.55);
}

@keyframes backpack-bounce-up {
	0% {
		transform: translateY(0);
	}
	30% {
		transform: translateY(-6px) scale(1.08);
	}
	60% {
		transform: translateY(2px) scale(0.98);
	}
	80% {
		transform: translateY(-4px) scale(1.03);
	}
	100% {
		transform: translateY(0) scale(1);
	}
}
</style>